import React, { useState } from "react";
import PropTypes from "prop-types";
import { Col, Row } from "react-bootstrap";
import { compose } from "recompose";
import { connect } from "react-redux";
import { useHistory, useLocation } from "react-router";
import { withOverlay } from "@dpdgroupuk/mydpd-ui";
import { trackProps, withTrack } from "@dpdgroupuk/react-event-tracker";
import { isEmpty } from "lodash";
import { SubmissionError, formValueSelector } from "redux-form";
import queryString from "query-string";
import CurrentDetailsForm from "./CurrentDetailsForm";
import NewPasswordForm from "./NewPasswordForm";
import HelperText from "../../../../components/PasswordChangeHelperText";
import { ROUTE, ANALYTICS, FORM } from "../../../../constants";
import PasswordChangeSuccess from "../PasswordChangeSuccess";
import { AuthHelpers, AuthErrors } from "../../../../features";
import { withAppContext } from "../../../../Context";
import styles from "./UpdatePasswordForm.module.scss";

const UpdatePasswordForm = ({
  onLogin,
  onUpdatePassword,
  username
}) => {
  const history = useHistory();
  const location = useLocation();
  const [passwordUpdated, setPasswordUpdated] = useState(false);
  const [previousUsername] = useState(location?.state?.username || "");
  const [previousPassword] = useState(location?.state?.password || "");
  const [isLogin, setIslLogin] = useState();

  if (isEmpty(location?.state?.username)) {
    history.replace({
      pathname: ROUTE.LOGON_PAGE,
      search: location.search
    });
    return null;
  }

  if (passwordUpdated) {
    return /*#__PURE__*/React.createElement(PasswordChangeSuccess, null);
  }

  const onSubmitCurrentDetails = async credentials => setIslLogin((await onLogin(credentials)));

  const onSubmitUpdatePassword = async ({
    password,
    newPassword,
    newPasswordConfirmation
  }) => {
    await onUpdatePassword(isLogin, password, newPassword, newPasswordConfirmation, username);
    setPasswordUpdated(true);
  };

  const onCancel = () => {
    return history.replace({
      pathname: ROUTE.LOGON_PAGE,
      search: location.search
    });
  };

  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("h1", {
    className: styles.title
  }, "Change your DPD password"), /*#__PURE__*/React.createElement(Row, null, /*#__PURE__*/React.createElement(Col, {
    xs: 12,
    md: 8,
    className: styles.forms
  }, /*#__PURE__*/React.createElement(Row, {
    className: styles.dividerMargin
  }, /*#__PURE__*/React.createElement(Col, {
    xs: 12,
    sm: 6
  }, /*#__PURE__*/React.createElement(CurrentDetailsForm, {
    previousUsername: previousUsername,
    previousPassword: previousPassword,
    onSubmit: onSubmitCurrentDetails,
    enabled: !isLogin,
    onCancel: onCancel
  })), /*#__PURE__*/React.createElement(Col, {
    xs: 12,
    sm: 6
  }, /*#__PURE__*/React.createElement(NewPasswordForm, {
    onSubmit: onSubmitUpdatePassword,
    enabled: !!isLogin,
    onCancel: onCancel
  })))), /*#__PURE__*/React.createElement(Col, {
    xs: 12,
    md: 4
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.divider
  }, !isLogin && /*#__PURE__*/React.createElement(HelperText, {
    text: "You\u2019ve come to the right place to change your password.<br /><br />For your security, we\u2019ll first need you to enter your current password to verify that you\u2019re the owner of this account."
  }), isLogin && /*#__PURE__*/React.createElement(HelperText, {
    text: "Next, we need create your new password.<br /><br />Please use at least one special character, number and capital letter."
  })))));
};

UpdatePasswordForm.propTypes = {
  onLogin: PropTypes.func,
  onUpdatePassword: PropTypes.func,
  username: PropTypes.string
};
const selector = formValueSelector(FORM.UPDATE_PASSWORD_FORM);
export default compose(withOverlay, withAppContext, connect(state => {
  const username = selector(state, "username");
  const password = selector(state, "password");
  return {
    username,
    password
  };
}, (dispatch, {
  app,
  overlay,
  location
}) => ({
  onLogin: overlay.showWhile(async ({
    username = "",
    password = ""
  }) => {
    try {
      const {
        carrier
      } = queryString.parse(location.search, {
        parseNumbers: true
      });
      const credentials = AuthHelpers.formatCredentials({
        username,
        password
      }, carrier);
      return await app.auth.signIn(credentials.username, credentials.password);
    } catch (e) {
      throw new SubmissionError({
        _error: AuthErrors.getUserAuthFormError(e)
      });
    }
  }),
  onUpdatePassword: overlay.showWhile(async (isLogin, password, newPassword, newPasswordConfirmation, username) => {
    try {
      const credentials = AuthHelpers.formatCredentials({
        username,
        password
      });
      const newCredentials = AuthHelpers.formatCredentials({
        username,
        password: newPassword
      });
      await app.auth.resetUsernamePassword(credentials.password, newCredentials.password);
    } catch (e) {
      throw new SubmissionError({
        _error: AuthErrors.getUserAuthFormError(e)
      });
    }
  })
})), withTrack(trackProps(ANALYTICS.UPDATE_PASSWORD_PAGE)))(UpdatePasswordForm);